// =============================================================
// Shared accessible modal dialog (focus-trap + Escape-to-close), extracted from
// the Settings dictionaries editor so the Wave-7 measurement screens reuse the
// exact same chrome. role="dialog" aria-modal, autofocus first control, Tab is
// trapped within the dialog. The scrim click closes.
// =============================================================

import { useEffect, useRef, type ReactNode } from "react";
import { Icon, ICONS } from "./primitives.js";

export function Modal({
  title,
  icon = ICONS.cog,
  onClose,
  children,
  testId,
  width = 480,
}: {
  title: string;
  icon?: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  width?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]:not([tabindex="-1"])')?.focus();
  }, []);
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          if (e.key !== "Tab") return;
          const focusables = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>(
              'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => !el.hasAttribute("disabled"));
          if (focusables.length === 0) return;
          const first = focusables[0]!;
          const last = focusables[focusables.length - 1]!;
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="modal-hd">
          <div className="ic" style={{ background: "var(--brand-wash)", color: "var(--brand-primary)" }}>
            <Icon d={icon} size={20} />
          </div>
          <h3>{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );
}
